import { useCallback, useEffect, useState } from "react";
import { allMovies } from "@/lib/catalog";
import type { Movie } from "@/lib/catalog";

const STORAGE_KEY = "jpf:favorites";
const SYNC_EVENT = "jpf:favorites-changed";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new Event(SYNC_EVENT));
  } catch {
    /* 本地存储不可用时静默降级，仅当前会话生效 */
  }
}

export function useFavorites() {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    setIds(readFavorites());
    const sync = () => setIds(readFavorites());
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    const current = readFavorites();
    const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
    writeFavorites(next);
    setIds(next);
    return next.includes(id);
  }, []);

  const remove = useCallback((id: string) => {
    const next = readFavorites().filter((item) => item !== id);
    writeFavorites(next);
    setIds(next);
  }, []);

  const clear = useCallback(() => {
    writeFavorites([]);
    setIds([]);
  }, []);

  const favoriteMovies: Movie[] = ids
    .map((id) => allMovies.find((movie) => movie.id === id))
    .filter((movie): movie is Movie => Boolean(movie));

  return { ids, favoriteMovies, toggle, remove, clear };
}